import { useEffect, useState } from "react";

export interface Question {
  question: string;
  question_slug: string;
  score: number;
  evidence: Array<{ source_url: string; excerpt: string; relevance: string }>;
}

export interface Dimension {
  dimension_slug: string;
  name: string;
  dimension_summary: string;
  dimension_justification: string;
  questions: Question[];
}

// Function to calculate the score for a single dimension
export const calculateDimensionScore = (dimension: Dimension): number => {
  if (!dimension.questions || dimension.questions.length === 0) {
    // return dimension.dimension_score; // Incorrect: dimension_score doesn't exist here yet
    return 0; // Return 0 if no questions are available for calculation
  }

  const sum = dimension.questions.reduce((total, q) => total + q.score, 0);
  return parseFloat((sum / dimension.questions.length).toFixed(1));
};

let baseUrl =
  "https://raw.githubusercontent.com/victordibia/multiagent-systems-with-autogen/refs/heads/main/research";

// baseUrl = "";
export interface FrameworkData {
  framework_name: string;
  framework_url: string;
  evaluation_date: string;
  framework_summary: string;
  dimensions: Dimension[];
  avgScore?: number;
  imageUrl?: string; // Added imageUrl property
}

// Metadata interface
interface FrameworkMetadata {
  id: string;
  name: string;
  file: string;
  imageUrl: string;
}

export function useFrameworkData() {
  const [frameworks, setFrameworks] = useState<FrameworkData[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    fetchData();
  }, []);

  async function fetchData() {
    try {
      setLoading(true);

      // First, fetch the metadata file which contains the list of all framework JSON files
      const metadataResponse = await fetch(
        `${baseUrl}/frameworks/metadata.json`
      );
      const metadataData = await metadataResponse.json();

      // Access the frameworks array from the metadata response
      const metadata: FrameworkMetadata[] = metadataData.frameworks || [];

      if (metadata.length === 0) {
        console.warn("No frameworks found in metadata file");
        setLoading(false);
        return;
      }

      // Fetch all framework data files in parallel
      const frameworkPromises = metadata.map(async (meta) => {
        const response = await fetch(`${baseUrl}/frameworks/${meta.file}`);
        const data: FrameworkData = await response.json();
        return {
          ...data,
          imageUrl: `${baseUrl}/frameworks/${meta.imageUrl}`,
        };
      });

      const frameworkData = await Promise.all(frameworkPromises);

      // Calculate scores based on question averages
      const withAvg = frameworkData
        .map((fw) => ({
          ...fw,
          dimensions: fw.dimensions.map((dim) => ({
            ...dim,
            dimension_score: calculateDimensionScore(dim),
          })),
        }))
        .map((fw) => ({
          ...fw,
          avgScore: parseFloat(
            (
              fw.dimensions.reduce(
                (sum: number, d: any) => sum + d.dimension_score,
                0
              ) / fw.dimensions.length
            ).toFixed(1)
          ),
        }));

      setFrameworks(withAvg);
      console.log("Loaded", withAvg.length, "frameworks");
    } catch (error) {
      console.error("Error loading framework data:", error);
    } finally {
      setLoading(false);
    }
  }

  return { frameworks, loading };
}
